<template>
  <SoSkeleton v-if="isLoading" class="w-50" :type="['text']" />

  <p v-else-if="isError">Something went wrong</p>

  <template v-else>
    <p class="mb-2">
      You are <strong v-if="!model.length">not </strong>eligible to switch to
      Smart Pay As You Go.
    </p>

    <template v-if="model.length">
      <p>Change how you manage your energy with our smart prepayment tariff.</p>
      <p>
        Pay upfront for the amount you want to use, track your usage, and top up
        when it suits you.
      </p>
    </template>

    <div class="mt-10">
      <SoRow>
        <SoCol
          v-for="{ meterpoint, spaygEligibility } in meterpointsEligibility"
          :key="meterpoint.id"
          :default-width="6"
        >
          <SoWidget full-height>
            <MeterpointHeader :meterpoint="meterpoint" class="mb-4" />

            <div data-testid="meterpoint-spayg-eligibility">
              <p v-if="!spaygEligibility">
                Meterpoint eligibility <strong>not found</strong>
              </p>
              <template v-else>
                <p>
                  Meterpoint is
                  <strong v-if="!spaygEligibility.eligibleForSpayg">not </strong
                  >eligible
                </p>
                <p v-if="!spaygEligibility.eligibleForSpayg">
                  Reason:
                  {{ spaygEligibility.ineligibleReason }}
                </p>

                <p
                  v-if="activeIdentifiers.includes(meterpoint.identifier)"
                  class="mt-4 font-weight-medium"
                >
                  This meterpoint is Smart Pay As You Go already
                </p>

                <p
                  v-if="upcomingIdentifiers.includes(meterpoint.identifier)"
                  class="mt-4 font-weight-medium"
                >
                  This meterpoint already applied for Smart Pay As You Go
                </p>
              </template>
            </div>
          </SoWidget>
        </SoCol>
      </SoRow>
    </div>
  </template>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import rulesValidators from "@/validators/rules"
  import { ValidationRule } from "@/types/ValidationRule"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import SoSkeleton from "@/components/SoSkeleton.vue"
  import SoRow from "@/components/SoRow.vue"
  import SoCol from "@/components/SoCol.vue"
  import MeterpointHeader from "@/views/customer/components/MeterpointHeader.vue"
  import SoWidget from "@/components/SoWidget.vue"

  export default defineComponent({
    components: {
      SoSkeleton,
      MeterpointHeader,
      SoRow,
      SoCol,
      SoWidget,
    },

    props: {
      modelValue: {
        type: Array as PropType<string[]>,
        required: true,
      },
      rules: {
        type: Array as PropType<ValidationRule[]>,
        default: () => [],
      },
      activeSpaygIdentifiers: {
        type: Array as PropType<string[]>,
        default: () => [],
      },
      upcomingSpaygIdentifiers: {
        type: Array as PropType<string[]>,
        default: () => [],
      },
    },

    emits: ["update:modelValue"],

    data() {
      return {
        rulesValidators,
        currentAccount: useCurrentAccountStore(),
      }
    },

    computed: {
      model: {
        get(): string[] {
          return Array.isArray(this.modelValue) ? this.modelValue : []
        },
        set(val: string[]) {
          this.$emit("update:modelValue", val)
        },
      },
      meterpointsData() {
        return this.currentAccount.meterpoints.data || []
      },
      spaygEligibilityData() {
        return this.currentAccount.spaygEligibility.data || []
      },
      // Every query behind this step, not just the two this component reads
      // directly. The flow resolves `modelValue`, `activeSpaygIdentifiers` and
      // `upcomingSpaygIdentifiers` from aggregates that also cover `account`,
      // `meterpointTariffs` and `meterpointAgreements`, and an aggregate hands
      // us `{}` instead of an array until all of its queries have settled. So
      // gating on only these two let the step render a definitive "not
      // eligible" from `{}` while the meterpoint cards — which read the store
      // directly — already showed the meterpoints as eligible (SO-32286).
      stepQueries() {
        const {
          account,
          spaygEligibility,
          meterpoints,
          meterpointTariffs,
          meterpointAgreements,
        } = this.currentAccount

        return [
          account,
          spaygEligibility,
          meterpoints,
          meterpointTariffs,
          meterpointAgreements,
        ]
      },
      isError() {
        // Surface a failure rather than letting absent data read as "not
        // eligible".
        return this.stepQueries.some((query) => query.isError)
      },
      isLoading() {
        // `isFetching` is false before a query has started, so on its own it
        // reports "loaded" while the data is still absent (SO-32286).
        return this.stepQueries.some(
          (query) => query.isFetching || !query.isFetched,
        )
      },
      // The flow resolves these props from query aggregates, which substitute an
      // empty object for `data` while loading. Coerce so the template's
      // `.includes` calls cannot fail on a non-array.
      activeIdentifiers(): string[] {
        return Array.isArray(this.activeSpaygIdentifiers)
          ? this.activeSpaygIdentifiers
          : []
      },
      upcomingIdentifiers(): string[] {
        return Array.isArray(this.upcomingSpaygIdentifiers)
          ? this.upcomingSpaygIdentifiers
          : []
      },
      eligibleMeterpoints() {
        if (this.isLoading) return []

        return this.meterpointsData
          .filter(
            (meterpoint) =>
              this.spaygEligibilityData.find(
                (eligibility) =>
                  eligibility.mpxnIdentifier === meterpoint.identifier,
              )?.eligibleForSpayg,
          )
          .map((meterpoint) => meterpoint.identifier)
      },
      meterpointsEligibility() {
        if (this.isLoading) return []

        return this.meterpointsData.map((meterpoint) => ({
          meterpoint: { ...meterpoint },
          spaygEligibility: this.spaygEligibilityData.find(
            (eligibility) =>
              eligibility.mpxnIdentifier === meterpoint.identifier,
          ),
        }))
      },
    },
    watch: {
      eligibleMeterpoints: {
        handler: function (val) {
          // Seeding the flow with an empty list while the queries are still
          // resolving is what left the step's button disabled (SO-32286).
          if (this.isLoading) return

          this.model = val || []
        },
        immediate: true,
      },
    },
  })
</script>
